import * as React from "react";
import TuneIcon from "@mui/icons-material/Tune";
import CloseIcon from "@mui/icons-material/Close";
import {
  Autocomplete,
  Badge,
  Box,
  Button,
  CircularProgress,
  Divider,
  Drawer,
  FormControl,
  IconButton,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { Stack } from "@mui/system";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import {
  colors,
  spacing,
  typography,
} from "../../../../../../../../../assets/styles/tokens";
import {
  selectUserData,
  selectUserToken,
} from "../../../../../../../../../features/userSlice";
import {
  resetOverviewConversionsMobileFilters,
  setOverviewConversionsMobileFiltersPartial,
  setOverviewConversionsNotesLabelFilter,
  updateOverviewConversionsMobileFilter,
} from "../../../../../../../../../features/bizDevelopmentDashboardMobileSlice";
import { BizDevelopmentDashboardMobileEndPointLogic } from "../../../../../../../../../logic/BizDevelopment/Dashboard/BizDevelopmentDashboardMobileEndPointLogic";
import { useOverviewConversionsMobile } from "./helpers/hooks/useOverviewConversionsMobile";
import {
  OverviewConversionsMobileDataSetQueryIds,
  OverviewConversionsMobileFilterQueryIds,
} from "./helpers/funcs/overviewConversionsMobileQueryIds";
import { VariableSizeList, ListChildComponentProps } from "react-window";

const Puller = styled("div")(({ theme }) => ({
  width: spacing.Spacing["4xl"].$value,
  height: spacing.Spacing.xs.$value,
  backgroundColor: colors.GrayLightMode[300].$value,
  borderRadius: spacing.Radius.xs.$value,
  position: "absolute",
  top: spacing.Spacing.xl.$value,
  left: "calc(50% - 15px)",
  ...theme.applyStyles("dark", {
    backgroundColor: colors.GrayLightMode[300].$value,
  }),
}));

const LISTBOX_PADDING = 8;
const ITEM_SIZE = 45;

const renderRow = ({ data, index, style }: ListChildComponentProps) => {
  const dataSet = data[index];
  return React.cloneElement(dataSet, {
    style: {
      ...style,
      top: (style.top as number) + LISTBOX_PADDING,
    },
  });
};

const OuterElementContext = React.createContext({});

const OuterElementType = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>((props, ref) => {
  const outerProps = React.useContext(OuterElementContext);
  return <div ref={ref} {...props} {...outerProps} />;
});
OuterElementType.displayName = "OuterElementType";

function useResetCache(data: number) {
  const ref = React.useRef<VariableSizeList>(null);
  React.useEffect(() => {
    if (ref.current != null) {
      ref.current.resetAfterIndex(0, true);
    }
  }, [data]);
  return ref;
}

export const ListboxComponent = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLElement>
>(function ListboxComponent(props, ref) {
  const { children, ...other } = props;
  const itemData: React.ReactElement[] = React.Children.toArray(
    children,
  ) as React.ReactElement[];
  const itemCount = itemData.length;

  const getHeight = () => {
    if (itemCount > 8) return 8 * ITEM_SIZE;
    return itemCount * ITEM_SIZE;
  };

  const gridRef = useResetCache(itemCount);

  return (
    <div ref={ref}>
      <OuterElementContext.Provider value={other}>
        <VariableSizeList
          itemData={itemData}
          height={getHeight() + 2 * LISTBOX_PADDING}
          width="100%"
          ref={gridRef}
          outerElementType={OuterElementType}
          innerElementType="ul"
          itemSize={() => ITEM_SIZE}
          overscanCount={5}
          itemCount={itemCount}
        >
          {renderRow}
        </VariableSizeList>
      </OuterElementContext.Provider>
    </div>
  );
});

interface Option {
  label: string;
  value: string;
}

const truncate = (text: string, limit = 18) => {
  if (!text) return "";
  return text.length > limit ? text.slice(0, limit) + "..." : text;
};

const isValidValue = (val: any) =>
  val !== null && val !== undefined && val !== "" && val !== "null";

const sharedAutocompleteSx = {
  minWidth: 184,
  maxWidth: 184,
  "& .MuiOutlinedInput-root": {
    borderRadius: 2,
    height: spacing.Spacing["4xl"].$value,
    py: "0 !important",
    fontSize: typography.Size.Text.sm.$value,
    fontWeight: typography.Font_weight.Semibold.$value,
    color: colors.GrayLightMode[700].$value,
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: colors.GrayLightMode[300].$value,
    },
    "&:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: colors.Brand[600].$value,
    },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: colors.Brand[600].$value,
    },
    "&.Mui-disabled": {
      background: "#E9EAEB",
      color: colors.GrayLightMode[500].$value,
    },
    "&.Mui-disabled .MuiOutlinedInput-notchedOutline": {
      borderColor: colors.GrayLightMode[300].$value,
    },
    "& .MuiAutocomplete-endAdornment .MuiSvgIcon-root": {
      color: colors.GrayLightMode[700].$value,
    },
  },
  "& .MuiAutocomplete-input": {
    padding: "7px !important",
    fontSize: typography.Size.Text.sm.$value,
  },
};

interface VirtualizedAutocompleteProps {
  options: Option[];
  value: Option | null;
  onChange: (val: Option | null) => void;
  placeholder: string;
  disabled?: boolean;
  loading?: boolean;
  onOpen?: () => void;
}

const VirtualizedAutocomplete: React.FC<VirtualizedAutocompleteProps> = ({
  options,
  value,
  onChange,
  placeholder,
  disabled = false,
  loading = false,
  onOpen,
}) => (
  <Autocomplete
    disableListWrap
    ListboxComponent={ListboxComponent}
    options={options}
    value={value}
    onChange={(_, newValue) => onChange(newValue)}
    onOpen={onOpen}
    disabled={disabled}
    loading={loading}
    isOptionEqualToValue={(opt, val) => opt.value === val.value}
    getOptionLabel={(opt) => opt.label}
    sx={sharedAutocompleteSx}
    renderInput={(params) => (
      <TextField
        {...params}
        placeholder={placeholder}
        size="small"
        InputProps={{
          ...params.InputProps,
          endAdornment: (
            <>
              {loading ? <CircularProgress color="inherit" size={14} /> : null}
              {params.InputProps.endAdornment}
            </>
          ),
        }}
        sx={{
          "& .MuiInputBase-input::placeholder": {
            fontSize: typography.Size.Text.sm.$value,
            color: colors.GrayLightMode[700].$value,
            opacity: 1,
          },
        }}
      />
    )}
    renderOption={(props, option) => (
      <li {...props} key={option.value}>
        {truncate(option.label)}
      </li>
    )}
    slotProps={{
      paper: {
        sx: {
          "& .MuiAutocomplete-listbox": {
            padding: 0,
          },
          "& .MuiMenuItem-root, & li": {
            fontSize: typography.Size.Text.sm.$value,
            minHeight: spacing.Spacing["5xl"].$value,
          },
        },
      },
    }}
  />
);

interface ConversionsFilterProps {
  dateType: string;
  endDate: string;
  startDate: string;
}

export default function ConversionsFilter({
  dateType,
  endDate,
  startDate,
}: ConversionsFilterProps) {
  const filters = useSelector(
    (state: any) =>
      state.bizDevelopmentDashboardMobile.overviewConversionsMobileFilters,
  );
  const dispatch = useDispatch();
  const [open, setOpen] = React.useState(false);
  const [filterLoading, setFilterLoading] = React.useState(false);
  const [searchParams] = useSearchParams();
  const oauthTokenHeader = useSelector(selectUserToken) as any;
  const response = useSelector(selectUserData);
  const websiteSharedUids = searchParams.get("workspace");
  const { fetchOverviewConversions } = useOverviewConversionsMobile({
    autoFetch: false,
  });
  const loadingOverviewConversionsMobileData = useSelector(
    (state: any) =>
      state.bizDevelopmentDashboardMobile?.loadingOverviewConversionsMobileData,
  );
  const filterDataByIdMobileMap = useSelector(
    (state: any) =>
      state.bizDevelopmentDashboardMobile
        ?.overviewConversionsFilterDataByIdMobileMap,
  );
  const trafficSourceDataMobile = useSelector(
    (state: any) =>
      state.bizDevelopmentDashboardMobile
        ?.overviewConversionsTrafficSourceDataMobile,
  );
  const geoDataMobile = useSelector(
    (state: any) =>
      state.bizDevelopmentDashboardMobile?.overviewConversionsGeoDataMobile,
  );
  const activityNotesData = useSelector(
    (state: any) =>
      state.bizDevelopmentDashboardMobile
        ?.overviewConversionsActivityNotesDataMobile,
  );
  const mobileFilters = useSelector(
    (state: any) =>
      state.bizDevelopmentDashboardMobile?.overviewConversionsMobileFilters,
  );
  const notesLabelFilter = useSelector(
    (state: any) =>
      state.bizDevelopmentDashboardMobile.overviewConversionsNotesLabelFilter,
  );

  const selectedDimensions = React.useMemo(() => {
    const uidList = websiteSharedUids?.split(",") ?? [];
    return (
      response?.data?.website_permissions?.flatMap((siteObj: any) => {
        const shared_uid = Object.keys(siteObj)[0];
        if (!uidList.includes(shared_uid)) return [];
        return (siteObj[shared_uid].dimensions ?? []).map(
          (dim: any) => dim.dimension_name,
        );
      }) ?? []
    );
  }, [response, websiteSharedUids]);

  const availableLabels = React.useMemo(() => {
    if (!Array.isArray(activityNotesData)) return [];
    const labelSet = new Set<string>();
    activityNotesData.forEach((note: any) => {
      if (Array.isArray(note?.labels)) {
        note.labels.forEach((label: string) => labelSet.add(label));
      }
    });
    return Array.from(labelSet).sort();
  }, [activityNotesData]);

  const trafficSourceOptions: Option[] = React.useMemo(
    () =>
      Array.isArray(trafficSourceDataMobile)
        ? trafficSourceDataMobile
            .filter((item) => isValidValue(item?.traffic_source_name))
            .map((item: any) => ({
              label: item.traffic_source_name,
              value: item.traffic_source_name,
            }))
        : [],
    [trafficSourceDataMobile],
  );

  const campaignData = React.useMemo(
    () => filterDataByIdMobileMap?.campaign?.query_output || [],
    [filterDataByIdMobileMap],
  );
  const adGroupData = React.useMemo(
    () => filterDataByIdMobileMap?.adgroup?.query_output || [],
    [filterDataByIdMobileMap],
  );
  const keywordData = React.useMemo(
    () => filterDataByIdMobileMap?.keyword?.query_output || [],
    [filterDataByIdMobileMap],
  );
  const isAllFiltersReady =
    Array.isArray(campaignData) &&
    campaignData.length > 0 &&
    Array.isArray(adGroupData) &&
    adGroupData.length > 0 &&
    Array.isArray(keywordData) &&
    keywordData.length > 0;

  const countryOptions: Option[] = React.useMemo(
    () =>
      Array.isArray(geoDataMobile)
        ? geoDataMobile
            .filter((item) => isValidValue(item?.name))
            .map((item: any) => ({ label: item.name, value: item.name }))
        : [],
    [geoDataMobile],
  );
  const campaignOptions: Option[] = React.useMemo(
    () =>
      Array.isArray(campaignData)
        ? campaignData
            .filter(
              (item) =>
                isValidValue(item?.camp_id) && isValidValue(item?.camp_name),
            )
            .map((item: any) => ({
              label: item.camp_name,
              value: String(item.camp_id),
            }))
        : [],
    [campaignData],
  );
  const adGroupOptions: Option[] = React.useMemo(
    () =>
      Array.isArray(adGroupData)
        ? adGroupData
            .filter(
              (item) =>
                isValidValue(item?.ag_id) && isValidValue(item?.ag_name),
            )
            .map((item: any) => ({
              label: item.ag_name,
              value: String(item.ag_id),
            }))
        : [],
    [adGroupData],
  );
  const keywordOptions: Option[] = React.useMemo(
    () =>
      Array.isArray(keywordData)
        ? keywordData
            .filter(
              (item) =>
                isValidValue(item?.kw_id) && isValidValue(item?.kw_name),
            )
            .map((item: any) => ({
              label: item.kw_name,
              value: String(item.kw_id),
            }))
        : [],
    [keywordData],
  );

  const toggleDrawer = (newOpen: boolean) => () => setOpen(newOpen);
  const isApplyDisabled = !filters.trafficSource;

  const fetchDependentFilters = React.useCallback(
    async (trafficSourceValue: string) => {
      const commonPayload = {
        oauthTokenHeader,
        websiteSharedUids: websiteSharedUids
          ? websiteSharedUids.split(",")
          : [],
        dimensions: selectedDimensions?.[0] ? [selectedDimensions[0]] : [],
        trafficSource: trafficSourceValue,
      };
      setFilterLoading(true);
      try {
        await Promise.all([
          BizDevelopmentDashboardMobileEndPointLogic.getOverviewConversionsFilterOptionsDataForCampaignAdgroupKeyword(
            {
              queryId: OverviewConversionsMobileFilterQueryIds.CAMP,
              key: "campaign",
              type: "site",
              account_shared_uid: 1,
              campaign_status: "all",
              ...commonPayload,
            },
          ),
          BizDevelopmentDashboardMobileEndPointLogic.getOverviewConversionsFilterOptionsDataForCampaignAdgroupKeyword(
            {
              queryId: OverviewConversionsMobileFilterQueryIds.ADGROUP,
              key: "adgroup",
              type: "site",
              ...commonPayload,
            },
          ),
          BizDevelopmentDashboardMobileEndPointLogic.getOverviewConversionsFilterOptionsDataForCampaignAdgroupKeyword(
            {
              queryId: OverviewConversionsMobileFilterQueryIds.KEYWORD,
              key: "keyword",
              type: "site",
              ...commonPayload,
            },
          ),
        ]);
      } finally {
        setFilterLoading(false);
      }
    },
    [oauthTokenHeader, websiteSharedUids, selectedDimensions],
  );

  const handleClearAll = () => {
    dispatch(resetOverviewConversionsMobileFilters());
    dispatch(setOverviewConversionsNotesLabelFilter(null));
    toggleDrawer(false)();
    fetchOverviewConversions({
      oauthTokenHeader,
      queryId: OverviewConversionsMobileDataSetQueryIds.CLICK,
      startDate,
      endDate,
      timeUnit: "day",
      type: "site",
      matchtype: "all",
      websiteSharedUids: websiteSharedUids ? websiteSharedUids.split(",") : [],
      dimensions: selectedDimensions?.[0] ? [selectedDimensions[0]] : [],
      trafficSource: "all",
      country: "all",
      campaignId: "1",
      adgroupId: "1",
      kwId: "1",
    });
  };

  const handleTrafficSourceChange = async (option: Option | null) => {
    dispatch(
      setOverviewConversionsMobileFiltersPartial({
        trafficSource: option,
        campaign: null,
        adGroup: null,
        keyword: null,
      }),
    );
    if (!option) return;
    await fetchDependentFilters(option.value);
  };

  const buildParams = () => {
    if (!filters.trafficSource) return null;
    return {
      oauthTokenHeader,
      queryId: OverviewConversionsMobileDataSetQueryIds.CLICK,
      startDate,
      endDate,
      timeUnit: "day",
      type: "site",
      matchtype: "all",
      websiteSharedUids: websiteSharedUids ? websiteSharedUids.split(",") : [],
      dimensions: selectedDimensions?.[0] ? [selectedDimensions[0]] : [],
      trafficSource: filters.trafficSource.value,
      country: filters.country?.value || "all",
      campaignId: filters.campaign?.value || "1",
      adgroupId: filters.adGroup?.value || "1",
      kwId: filters.keyword?.value || "1",
    };
  };

  const handleApply = async () => {
    const params = buildParams();
    if (!params) {
      console.error("Traffic source is required");
      return;
    }
    toggleDrawer(false)();
    await fetchOverviewConversions(params);
  };

  const hasActiveFilters = React.useMemo(() => {
    const mobileHasValue = Object.values(mobileFilters || {}).some(
      (val) => val !== null && val !== undefined,
    );
    return mobileHasValue || !!notesLabelFilter;
  }, [mobileFilters, notesLabelFilter]);

  const sharedSelectSx = {
    borderRadius: 2,
    height: spacing.Spacing["4xl"].$value,
    minWidth: 184,
    maxWidth: 184,
    py: spacing.Spacing.sm.$value,
    fontSize: typography.Size.Text.sm.$value,
    lineHeight: typography.Line_height.text.sm.$value,
    fontWeight: typography.Font_weight.Semibold.$value,
    color: colors.GrayLightMode[700].$value,
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: colors.GrayLightMode[300].$value,
    },
    "&:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: colors.Brand[600].$value,
    },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: colors.Brand[600].$value,
    },
    "& .MuiSelect-icon": { color: colors.GrayLightMode[700].$value },
    "&.Mui-disabled": {
      background: "#E9EAEB",
      color: colors.GrayLightMode[500].$value,
    },
    "&.Mui-disabled .MuiOutlinedInput-notchedOutline": {
      borderColor: colors.GrayLightMode[300].$value,
    },
  };

  const labelTypoSx = {
    fontSize: typography.Size.Text.sm.$value,
    lineHeight: typography.Line_height.text.sm.$value,
  };

  return (
    <>
      <IconButton
        color="secondary"
        size="large"
        onClick={toggleDrawer(true)}
        disabled={loadingOverviewConversionsMobileData}
      >
        <Badge
          color="secondary"
          sx={{
            "& .MuiBadge-badge": {
              height: spacing.Spacing.lg.$value,
              width: spacing.Spacing.lg.$value,
              borderRadius: spacing.Radius.full.$value,
              backgroundColor: colors.Brand[600].$value,
            },
          }}
          variant="dot"
          invisible={!hasActiveFilters}
        >
          <TuneIcon sx={{ color: colors.GrayLightMode[700].$value }} />
        </Badge>
      </IconButton>

      <Drawer
        anchor="bottom"
        open={open}
        onClose={toggleDrawer(false)}
        PaperProps={{
          sx: {
            borderTopLeftRadius: spacing.Radius.xl.$value,
            borderTopRightRadius: spacing.Radius.xl.$value,
            p: spacing.Spacing.xl.$value + "!important",
          },
        }}
      >
        <Box>
          <Puller />
          <Stack
            justifyContent="space-between"
            direction="row"
            mt={1.5}
            mb={1}
            alignItems="center"
          >
            <Typography
              variant="body1"
              sx={{
                fontSize: typography.Size.Text.xs.$value,
                lineHeight: typography.Line_height.text.xs.$value,
                color: colors.GrayLightMode[700].$value,
              }}
            >
              Filter by
            </Typography>
            <Typography
              onClick={handleClearAll}
              sx={{
                fontSize: typography.Size.Text.xs.$value,
                lineHeight: typography.Line_height.text.xs.$value,
                color: colors.GrayLightMode[700].$value,
                fontWeight: typography.Font_weight.Semibold.$value,
                textTransform: "capitalize",
                letterSpacing: 0,
                p: 0,
                cursor: "pointer",
              }}
            >
              Clear All
            </Typography>
          </Stack>

          <Box sx={{ my: spacing.Spacing.lg.$value }}>
            <Stack
              alignItems="flex-start"
              justifyContent="space-between"
              flexDirection="row"
            >
              <Typography
                color={colors.GrayLightMode[700].$value}
                sx={{ ...labelTypoSx, mt: "5px" }}
              >
                Note Labels
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-end",
                }}
              >
                <FormControl size="small">
                  <Select
                    displayEmpty
                    value={notesLabelFilter ?? ""}
                    onChange={(e) =>
                      dispatch(
                        setOverviewConversionsNotesLabelFilter(
                          e.target.value || null,
                        ),
                      )
                    }
                    endAdornment={
                      notesLabelFilter ? (
                        <IconButton
                          size="small"
                          onClick={() =>
                            dispatch(
                              setOverviewConversionsNotesLabelFilter(null),
                            )
                          }
                          sx={{
                            mr: 1.5,
                            p: "2px",
                            color: colors.GrayLightMode[500].$value,
                            "&:hover": {
                              color: colors.GrayLightMode[700].$value,
                              background: "transparent",
                            },
                          }}
                        >
                          <CloseIcon sx={{ fontSize: 20 }} />
                        </IconButton>
                      ) : null
                    }
                    MenuProps={{
                      PaperProps: {
                        sx: {
                          "& .MuiMenuItem-root": {
                            fontSize: typography.Size.Text.sm.$value,
                            minHeight: spacing.Spacing["5xl"].$value,
                          },
                        },
                      },
                    }}
                    sx={sharedSelectSx}
                  >
                    {availableLabels.length === 0 ? (
                      <MenuItem value="" disabled>
                        No notes available
                      </MenuItem>
                    ) : (
                      <MenuItem value="">Select Notes Label</MenuItem>
                    )}
                    {availableLabels.map((label) => (
                      <MenuItem key={label} value={label}>
                        {label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Box sx={{ minHeight: "18px", mt: "4px" }}>
                  {notesLabelFilter && (
                    <Typography
                      sx={{
                        fontSize: typography.Size.Text.xxs.$value,
                        lineHeight: typography.Line_height.text.xxs.$value,
                        color: colors.Brand[600].$value,
                        fontWeight: typography.Font_weight.Medium.$value,
                        mt: "4px",
                      }}
                    >
                      Notes filtered by "{notesLabelFilter}"
                    </Typography>
                  )}
                </Box>
              </Box>
            </Stack>

            <Divider
              sx={{
                my: spacing.Spacing.sm.$value,
                borderWidth: spacing.Spacing.xxs.$value,
                borderColor: colors.GrayLightMode[100].$value,
                borderRadius: spacing.Radius.xxs.$value,
              }}
            />

            {dateType === "click_date" ? (
              <>
                {!filters.trafficSource && (
                  <Typography
                    sx={{
                      fontSize: "0.7rem",
                      color: "#6b6b6b",
                      background:
                        "linear-gradient(90deg, #5a5a5aff 25%, #bebdbdff 50%, #5a5a5aff 75%)",
                      backgroundSize: "300% 100%",
                      backgroundClip: "text",
                      WebkitBackgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                      animation: "shimmer 8s linear infinite",
                      paddingBottom: "10px",
                    }}
                  >
                    Select traffic source to enable "Apply" and other filters.
                  </Typography>
                )}
                <Stack
                  alignItems="center"
                  justifyContent="space-between"
                  flexDirection="row"
                >
                  <Typography
                    color={colors.GrayLightMode[700].$value}
                    sx={labelTypoSx}
                  >
                    Traffic Source
                  </Typography>
                  <FormControl size="small">
                    <VirtualizedAutocomplete
                      options={trafficSourceOptions}
                      value={filters.trafficSource}
                      onChange={handleTrafficSourceChange}
                      placeholder="Select Traffic Source"
                    />
                  </FormControl>
                </Stack>
                <Stack
                  alignItems="center"
                  justifyContent="space-between"
                  flexDirection="row"
                  sx={{ my: spacing.Spacing.lg.$value }}
                >
                  <Typography
                    color={colors.GrayLightMode[700].$value}
                    sx={labelTypoSx}
                  >
                    Country
                  </Typography>
                  <VirtualizedAutocomplete
                    options={countryOptions}
                    value={filters.country}
                    onChange={(val) =>
                      dispatch(
                        updateOverviewConversionsMobileFilter({
                          key: "country",
                          value: val,
                        }),
                      )
                    }
                    placeholder="Select Country"
                    disabled={!isAllFiltersReady || filterLoading}
                    loading={filterLoading}
                  />
                </Stack>
                <Stack
                  alignItems="center"
                  justifyContent="space-between"
                  flexDirection="row"
                  sx={{ my: spacing.Spacing.lg.$value }}
                >
                  <Typography
                    color={colors.GrayLightMode[700].$value}
                    sx={labelTypoSx}
                  >
                    Campaign
                  </Typography>
                  <VirtualizedAutocomplete
                    options={campaignOptions}
                    value={filters.campaign}
                    onChange={(val) =>
                      dispatch(
                        updateOverviewConversionsMobileFilter({
                          key: "campaign",
                          value: val,
                        }),
                      )
                    }
                    placeholder="Select Campaign"
                    disabled={!isAllFiltersReady || filterLoading}
                    loading={filterLoading}
                  />
                </Stack>
                <Stack
                  alignItems="center"
                  justifyContent="space-between"
                  flexDirection="row"
                  sx={{ my: spacing.Spacing.lg.$value }}
                >
                  <Typography
                    color={colors.GrayLightMode[700].$value}
                    sx={labelTypoSx}
                  >
                    Ad Group
                  </Typography>
                  <VirtualizedAutocomplete
                    options={adGroupOptions}
                    value={filters.adGroup}
                    onChange={(val) =>
                      dispatch(
                        updateOverviewConversionsMobileFilter({
                          key: "adGroup",
                          value: val,
                        }),
                      )
                    }
                    placeholder="Select Ad Group"
                    disabled={!isAllFiltersReady || filterLoading}
                    loading={filterLoading}
                  />
                </Stack>
                <Stack
                  alignItems="center"
                  justifyContent="space-between"
                  flexDirection="row"
                  sx={{ my: spacing.Spacing.lg.$value }}
                >
                  <Typography
                    color={colors.GrayLightMode[700].$value}
                    sx={labelTypoSx}
                  >
                    Keyword
                  </Typography>
                  <VirtualizedAutocomplete
                    options={keywordOptions}
                    value={filters.keyword}
                    onChange={(val) =>
                      dispatch(
                        updateOverviewConversionsMobileFilter({
                          key: "keyword",
                          value: val,
                        }),
                      )
                    }
                    placeholder="Select Keyword"
                    disabled={!isAllFiltersReady || filterLoading}
                    loading={filterLoading}
                  />
                </Stack>
              </>
            ) : null}
          </Box>
          {dateType === "action_date" ? null : (
            <Button
              fullWidth
              variant="contained"
              disabled={isApplyDisabled}
              onClick={handleApply}
              sx={{
                textTransform: "capitalize",
                boxShadow: "none !important",
                background: colors.Brand[600].$value,
                borderRadius: 2,
                mt: spacing.Spacing.lg.$value,
              }}
            >
              Apply
            </Button>
          )}
        </Box>
      </Drawer>
    </>
  );
}
